import { useEffect, useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { CURRENCY_TO_COUNTRY, isActivatableWalletCurrency } from "../constants/wallets.constants";
import { useWalletActivation } from "../hooks/useWalletActivation";

interface FormValues {
  currency: string;
}

export interface ActivateWalletModalProps {
  open: boolean;
  onClose: () => void;
  /** Currencies the org already activated — filtered out of the selector so it only offers new ones. */
  activatedCurrencies: string[];
  /** Called after a successful activation so the caller can refetch the wallet list. */
  onActivated: () => void;
  /** INTERNAL-only: activate on behalf of this client org. Members omit it (server-scoped to their org). */
  organizationId?: string;
}

/**
 * Org-member wallet activation (handoff §3.1 + §3.2): pick a currency from the backend catalog and POST to
 * create it. The catalog arrives whole and is narrowed twice before it is offered: by the currencies already
 * activated, and by `ACTIVATABLE_WALLET_CURRENCIES` — the provisional gate that keeps the selector to the
 * four automated corridors. The second filter is a product decision, not a backend one; the endpoint still
 * accepts anything the catalog returns.
 */
export function ActivateWalletModal({
  open,
  onClose,
  activatedCurrencies,
  onActivated,
  organizationId,
}: ActivateWalletModalProps) {
  const { t } = useTranslations("wallets");
  const { t: tGlobal } = useTranslations("global");
  const { currencies, isLoadingCurrencies, isActivating, loadCurrencies, activate } = useWalletActivation();
  const schema = useMemo(
    () => z.object({ currency: z.string().min(1, tGlobal("input_error_required")) }),
    [tGlobal],
  );
  const form = useZodForm(schema, { currency: "" });

  useEffect(() => {
    if (!open) return;
    void loadCurrencies();
    form.reset({ currency: "" });
  }, [open, loadCurrencies, form]);

  const items = useMemo(() => {
    const already = new Set(activatedCurrencies.map((c) => c.toUpperCase()));
    return currencies
      .filter((currency) => isActivatableWalletCurrency(currency) && !already.has(currency.toUpperCase()))
      .map((currency) => ({
        value: currency,
        label: CURRENCY_TO_COUNTRY[currency] ? `${currency} · ${CURRENCY_TO_COUNTRY[currency]}` : currency,
      }));
  }, [currencies, activatedCurrencies]);
  // Measured against the GATED catalog, never the raw one: "you have activated every available currency"
  // has to mean every currency this screen offers. Counting the raw catalog would keep the message hidden
  // for an org that already holds the four, because the backend still lists the seven withheld ones.
  const offerableCount = useMemo(
    () => currencies.filter((c) => isActivatableWalletCurrency(c)).length,
    [currencies],
  );
  const allActivated = !isLoadingCurrencies && offerableCount > 0 && items.length === 0;

  const onSubmit = async (values: FormValues) => {
    try {
      await activate(values.currency, organizationId);
      colocaToast.success(t("activate_success"));
      onActivated();
      onClose();
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("activate_error"));
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isActivating) onClose();
      }}
      title={t("activate_title")}
      description={t("activate_subtitle")}
      /*
       * Every other wallets modal (Argentina, Brazil, Colombia, Mexico deposits) is fullscreen on a phone;
       * this one was the exception, and it is the only one that could drift out of view. A centred dialog is
       * placed with `-translate-y-1/2` over its own height and capped at `100dvh`, and `dvh` re-resolves every
       * time Chrome Android collapses or restores the URL bar — so opening the currency popup (which does not
       * scroll-lock when opened by touch) left the panel somewhere else on the page. Anchored to the edge
       * there is no vertical centring left to recompute.
       */
      mobileFullscreen
    >
      <ColocaForm form={form} onSubmit={onSubmit}>
        <ColocaSelectField
          name="currency"
          label={t("activate_currency_label")}
          placeholder={isLoadingCurrencies ? t("activate_loading") : t("activate_currency_placeholder")}
          items={items}
          disabled={allActivated}
        />
        {allActivated && <p className="text-xs text-muted-foreground">{t("activate_all_active")}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isActivating}>
            {t("activate_cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            variant="primary"
            loading={isActivating}
            disabled={isLoadingCurrencies || allActivated}
          >
            {t("activate_confirm")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}
